/* -----------------Globals--------------- */
import { memo, useCallback } from "react";
import isEqual from "react-fast-compare";
import { useTranslation } from "react-i18next";

import { useHotkeys } from "@memorang/helpers/src/hooks/useHotkeys";
/* -----------------Child components--------------- */
import Box from "@memorang/ui/src/components/Box";
import { RecommendedChips } from "@memorang/ui/src/components/ChatUI/RecommendedChips";
import { RecommendedChipsSkeleton } from "@memorang/ui/src/components/ChatUI/RecommendedChipsSkeleton";
import { CustomListSubheader } from "@memorang/ui/src/components/CustomListSubheader";
import { useDevice } from "@memorang/ui/src/useDevice";
/* -----------------Helpers & Hooks--------------- */
import { useRecommendedQueries } from "./hooks/useRecommendedQueries";
/* -----------------Types--------------- */
import type { VectorStoreParams } from "./types";

type Props = {
	userPrompt: string;
	vectorStoreParams: VectorStoreParams;
	showCustomResponse?: boolean;
	userId: string;
	handleChipPress?: (query: string) => void;
	showDialog: boolean;
	setShowDialog: (show: boolean) => void;
	setSelectedQuery: (query: string) => void;
	showKeyboardShortcuts?: boolean;
	visible?: boolean;
	loading?: boolean;
	outputLanguage: string;
	showTitleSubheader?: boolean;
	appId?: string;
};

const QAChips = ({
	vectorStoreParams,
	userPrompt,
	showTitleSubheader = true,
	showCustomResponse = true,
	userId,
	handleChipPress,
	showDialog,
	setShowDialog,
	setSelectedQuery,
	showKeyboardShortcuts = true,
	visible = true,
	loading,
	outputLanguage,
}: Props) => {
	const { t } = useTranslation();

	const { isDesktopWeb } = useDevice();

	const { recommendedQueries, isLoading: isRecommendedQueriesLoading } =
		useRecommendedQueries({
			userPrompt,
			vectorStoreParams,
			userId,
			loading,
			outputLanguage,
		});

	const isLoading = loading || isRecommendedQueriesLoading;

	const handleSelectQuery = useCallback(
		(query: string): void => {
			setSelectedQuery(query);
			if (handleChipPress) {
				handleChipPress(query);
			}
			if (isDesktopWeb) {
				setShowDialog(true);
			}
		},
		[handleChipPress, setSelectedQuery, setShowDialog, isDesktopWeb],
	);

	const handleHotkeyAction = useCallback(
		(index: number) => {
			if (
				!isLoading &&
				recommendedQueries.length > 0 &&
				recommendedQueries[index]
			) {
				handleSelectQuery(recommendedQueries[index]);
			}
		},
		[recommendedQueries, isLoading, handleSelectQuery],
	);

	useHotkeys(
		// to give priority to hotkeys in AiLA, we have this condition
		!showDialog && showKeyboardShortcuts
			? [
					["mod+1", () => handleSelectQuery("")],
					["mod+2", () => handleHotkeyAction(0)],
					["mod+3", () => handleHotkeyAction(1)],
					["mod+4", () => handleHotkeyAction(2)],
				]
			: [],
		[],
	);

	if (!visible) {
		return null;
	}

	return (
		<Box gap={16}>
			{showTitleSubheader && (
				<CustomListSubheader
					style={{
						paddingLeft: 0,
					}}
					textVariant="titleMedium"
				>
					{t("session.followUps")}
				</CustomListSubheader>
			)}
			{isLoading ? (
				<RecommendedChipsSkeleton numLines={4} />
			) : (
				<RecommendedChips
					handleSelectQuery={handleSelectQuery}
					queries={recommendedQueries}
					showCustomResponse={showCustomResponse}
					noFlatList
				/>
			)}
		</Box>
	);
};

export default memo(QAChips, isEqual);
